<script setup lang="ts">
import { useCookieConsent } from "@/composables/useCookieConsent"
import { useNavbarState } from "@/stores"
import { useLocaleChooser } from "@/stores/useLocaleChooser"
import { useAppConfig } from "@/vf"
import { onClickOutside } from "@vueuse/core"
import "flag-icons/css/flag-icons.min.css"
import { storeToRefs } from "pinia"
import { type Ref, ref } from "vue"

const navbarState = useNavbarState()
const { showMobileSidebar } = storeToRefs(navbarState)
const { toggleMobileSidebar } = navbarState

const { appConfig } = useAppConfig()
const { openOverlay: openCookieConsentOverlay } = useCookieConsent()

const localeChooser = useLocaleChooser()
const { currentLocale, showFakeLocale } = storeToRefs(localeChooser)
const { switchLocale, fakeLocale } = localeChooser

const localeDropdownOpen = ref(false)
const localeContainer: Ref<HTMLElement> = ref(null!)
onClickOutside(localeContainer, () => {
    localeDropdownOpen.value = false
})

function flagCode(iso: string): string {
    const localeToFlag: Record<string, string> = { en: "gb" }
    return localeToFlag[iso] ?? iso
}

function selectLocale(iso: string, fallback: string) {
    switchLocale(iso, fallback)
    localeDropdownOpen.value = false
}
</script>

<template>
    <div class="vb-mobile-header-container">
        <div class="vbmh-first-row">
            <div class="vbmh-search">
                <RouterLink :to="{ name: '@App.Search.results' }">
                    <i class="fa fa-fw fa-search"></i>
                    {{ $t("@app:header.search") }}
                </RouterLink>
            </div>
            <div ref="localeContainer" class="vbmh-login vbmh-locale" @click="localeDropdownOpen = !localeDropdownOpen">
                <a href="#">
                    <i class="fa fa-fw fa-globe-europe mr-1"></i>
                    {{ currentLocale?.toUpperCase() }}
                </a>
                <div v-if="localeDropdownOpen" class="vbmh-locale-dropdown">
                    <a
                        v-for="locale in appConfig.languages"
                        :key="locale.iso"
                        href="#"
                        :class="{ active: locale.iso == currentLocale }"
                        @click.stop="selectLocale(locale.iso, 'en')"
                    >
                        <span class="fi" :class="`fi-${flagCode(locale.iso)}`"></span>
                        {{ locale.name }}
                    </a>
                    <a
                        v-if="showFakeLocale"
                        href="#"
                        class="vbmh-locale-keys"
                        :class="{ active: fakeLocale == currentLocale }"
                        @click.stop="selectLocale(fakeLocale, fakeLocale)"
                    >
                        Language Keys
                    </a>
                </div>
            </div>
            <div class="vbmh-login vbmh-settings" @click="openCookieConsentOverlay">
                <a href="#"><i class="fa fa-cog"></i></a>
            </div>
        </div>
        <div class="vbmh-second-row">
            <RouterLink class="vbmh-logo" to="/">
                <img src="/assets/varsity-v-color.png" />
            </RouterLink>
            <button class="vbmh-menu-button" @click="toggleMobileSidebar()">
                <i class="fa fa-fw" :class="showMobileSidebar ? 'fa-times-circle' : 'fa-bars'"></i>
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import "@/styles/variables";

.vb-mobile-header-container {
    .vbmh-first-row {
        display: flex;
        height: 3rem;

        .vbmh-search,
        .vbmh-login {
            flex: 1;
            background-color: #0b6fb9;
            color: #fff;
            display: flex;
            align-items: stretch;
            justify-content: stretch;

            a {
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
                color: #fff;
                font-size: 0.75rem;
                font-weight: bold;
            }
        }

        .vbmh-login {
            position: relative;
        }

        .vbmh-locale-dropdown {
            position: absolute;
            top: 100%;
            left: -12px;
            right: 12px;
            z-index: 100;
            background-color: #084e82;
            display: flex;
            flex-direction: column;

            a {
                padding: 0.5rem 1rem;
                font-size: 0.75rem;
                font-weight: bold;
                justify-content: left;

                .fi {
                    display: inline-block;
                    width: 1.333em;
                    margin-right: 0.5rem;
                }

                &.vbmh-locale-keys::before {
                    font-family: "Font Awesome 6 Free";
                    font-weight: 900;
                    content: "\f0ad";
                    display: inline-block;
                    width: 1.333em;
                    text-align: center;
                    margin-right: 0.5rem;
                }

                &.active {
                    background-color: #0b6fb9;
                }
            }
        }

        .vbmh-login:before {
            content: "";
            position: absolute;
            width: 42px;
            height: 100%;
            top: 0;
            background: linear-gradient(to right, #084e82 0%, #0b6fb9 100%);
            transform: skew(-30deg);
        }

        .vbmh-settings {
            flex: 0 0;
            min-width: 7rem;
        }
    }

    .vbmh-second-row {
        display: grid;
        grid-template-columns: 1fr 5rem;
        grid-auto-rows: 3rem;

        .vbmh-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            grid-row: 1;
            grid-column: 1 / to 2;

            img {
                height: 1.5rem;
            }
        }

        .vbmh-menu-button {
            appearance: none;
            background-color: #dbe5ed;
            border: none;
            grid-row: 1;
            grid-column: 2;

            display: flex;
            align-items: center;
            justify-content: center;

            padding-left: 2rem;
            clip-path: polygon(2rem 0, 100% 0, 100% 100%, 0 100%);

            i {
                font-size: 1.5rem;
            }
        }
    }

    @include media-breakpoint-up(lg) {
        display: none;
    }
}
</style>
